/**
 * AI LAYOUT/DESIGN PASS (report-ai-design-pass · Phase 3 — engine consumption).
 *
 * Fold the plan's per-section `breakHint` decisions into the per-report
 * `pageMapOverrides` shape the engine ALREADY consumes
 * (`resolvePageMap` → `applyPageMapOverrides`). This is the ONE break seam: the
 * plan never carries page numbers (decisions, not pixels) — it only nudges the
 * existing `startMode` lever.
 *
 * `breakHint` → `startMode` mapping (see `ReportLayoutSectionPlan.breakHint`):
 *   - `'allow-break'`    → `{ startMode: 'auto' }`     — the engine may break freely;
 *   - `'start-page'`     → `{ startMode: 'page-pin' }` — begin on a fresh page;
 *   - `'keep-with-next'` → NO override — the engine's heading-keep CSS already
 *     holds the heading with its following content; an override here would be a
 *     resolver no-op (`applyPageMapOverrides` discards empty entries).
 *
 * OVERRIDE-FLOOR semantics live at the CALL SITE: the caller spreads the
 * report's OWN `pageMapOverrides` (an explicit user choice) AFTER this map, so a
 * user override wins on key collision and the AI never masquerades as a user
 * decision.
 *
 * CRITICAL ABSENT-PLAN INVARIANT: returns `undefined` when the plan is absent or
 * carries no actionable `breakHint`, so the caller keeps the report's ORIGINAL
 * `pageMapOverrides` reference intact — preserving `resolvePageMap`'s
 * byte-identical no-override fast path. The mapping is therefore additive and
 * inert until a plan with a real break hint is present.
 */
import type { PageMapOverride } from '../templatePages/resolvePageMap';
import type { ReportLayoutPlan } from '../../types/reportLayoutPlan';

export function buildLayoutPlanPageMapOverrides(
  plan: ReportLayoutPlan | undefined | null,
): Record<string, PageMapOverride> | undefined {
  const sections = plan?.sections;
  if (!sections) return undefined;

  let out: Record<string, PageMapOverride> | undefined;
  for (const [sectionId, sectionPlan] of Object.entries(sections)) {
    let startMode: PageMapOverride['startMode'] | undefined;
    switch (sectionPlan?.breakHint) {
      case 'allow-break':
        startMode = 'auto';
        break;
      case 'start-page':
        startMode = 'page-pin';
        break;
      // 'keep-with-next' (and absent) ⇒ no resolver override (heading-keep CSS).
      default:
        startMode = undefined;
    }
    if (startMode === undefined) continue;
    (out ??= {})[sectionId] = { startMode };
  }
  return out;
}
